import React, { forwardRef, isValidElement, cloneElement } from 'react';
import type { ReactNode, Ref } from 'react';

import { cssMap, cx } from '@atlaskit/css';
import DropdownItem from '@atlaskit/dropdown-menu/dropdown-menu-item';
import type { CustomItemComponentProps } from '@atlaskit/menu/types';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { Anchor, Box, Pressable } from '@atlaskit/primitives/compiled';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import { token } from '@atlaskit/tokens';

import type { DataAttributes } from '../types';

import { useToolbarDropdownMenu } from './ToolbarDropdownMenuContext';

type TextStyle =
	| 'normal'
	| 'heading1'
	| 'heading2'
	| 'heading3'
	| 'heading4'
	| 'heading5'
	| 'heading6';

const styles = cssMap({
	toolbarDropdownItem: {
		display: 'flex',
		position: 'relative',
		backgroundColor: token('color.background.neutral.subtle'),
		width: '100%',
		minHeight: '32px',
		minWidth: '225px',
		paddingInline: token('space.150'),
		paddingBlock: token('space.050'),
		color: token('color.text.subtle'),
		font: token('font.body'),
		'&:focus-visible': {
			outlineOffset: token('space.negative.025'),
			borderRadius: token('radius.small'),
		},
	},
	anchor: {
		textDecoration: 'none',
		'&:hover': {
			color: token('color.text.subtle'),
			textDecoration: 'none',
		},
		'&:visited': {
			color: token('color.text.subtle'),
		},
	},
	enabled: {
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
		'&:active': {
			backgroundColor: token('color.background.neutral.subtle.pressed'),
		},
	},
	disabled: {
		color: token('color.text.disabled'),
		cursor: 'not-allowed',
	},
	selected: {
		backgroundColor: token('color.background.selected'),
		color: token('color.text.selected'),
		'&:hover': {
			backgroundColor: token('color.background.selected.hovered'),
		},
		'&:active': {
			backgroundColor: token('color.background.selected.pressed'),
		},
	},
	elemAfterText: {
		marginLeft: token('space.075'),
	},
});

export type ToolbarDropdownItemInteractionProps = {
	onBlur?: React.FocusEventHandler;
	onFocus?: React.FocusEventHandler;
	onMouseEnter?: React.MouseEventHandler;
	onMouseLeave?: React.MouseEventHandler;
};

export type ToolbarDropdownItemRole = 'menuitem' | 'menuitemcheckbox' | 'menuitemradio';

export type CustomDropdownMenuItemButtonProps = CustomItemComponentProps &
	ToolbarDropdownItemInteractionProps & {
		'aria-disabled'?: boolean;
		'aria-haspopup'?: boolean;
		'aria-keyshortcuts'?: string;
		'aria-pressed'?: boolean;
		'data-extension-item-key'?: string;
		itemRole?: ToolbarDropdownItemRole;
		title?: string;
	};

const CustomDropdownMenuItemButton = forwardRef<
	HTMLButtonElement,
	CustomDropdownMenuItemButtonProps
>(
	(
		{
			children,
			'data-testid': testId,
			'aria-haspopup': ariaHasPopup,
			'aria-disabled': ariaDisabled,
			'aria-pressed': ariaPressed,
			'aria-keyshortcuts': ariaKeyshortcuts,
			onClick,
			onBlur,
			onFocus,
			onMouseEnter,
			onMouseLeave,
			tabIndex,
			title,
			'data-extension-item-key': dataExtensionItemKey,
			itemRole,
		},
		ref,
	) => (
		<Pressable
			role={
				expValEquals('platform_editor_layout_column_menu', 'isEnabled', true)
					? itemRole || 'menuitem'
					: expValEquals('platform_editor_enghealth_a11y_jan_fixes', 'isEnabled', true)
						? 'menuitem'
						: undefined
			}
			testId={testId as string}
			xcss={cx(
				styles.toolbarDropdownItem,
				ariaDisabled ? styles.disabled : ariaPressed ? styles.selected : styles.enabled,
			)}
			onClick={onClick}
			onBlur={onBlur}
			onFocus={onFocus}
			onMouseEnter={onMouseEnter}
			onMouseLeave={onMouseLeave}
			tabIndex={tabIndex}
			aria-haspopup={ariaHasPopup}
			aria-expanded={ariaHasPopup ? (ariaPressed ? true : false) : undefined}
			aria-checked={
				expValEquals('platform_editor_layout_column_menu', 'isEnabled', true) &&
				(itemRole === 'menuitemradio' || itemRole === 'menuitemcheckbox')
					? ariaPressed
					: undefined
			}
			// platform_editor_enghealth_a11y_jan_fixes: menuitem roles cannot have aria-pressed attribute
			aria-pressed={
				expValEquals('platform_editor_layout_column_menu', 'isEnabled', true)
					? undefined
					: expValEquals('platform_editor_enghealth_a11y_jan_fixes', 'isEnabled', true)
						? undefined
						: ariaPressed
			}
			aria-disabled={ariaDisabled}
			aria-keyshortcuts={ariaKeyshortcuts}
			data-toolbar-component="menu-item"
			ref={ref}
			title={title}
			data-extension-item-key={fg('cc_blocks_changeboarding') ? dataExtensionItemKey : undefined}
		>
			{children}
		</Pressable>
	),
);

type ToolbarDropdownItemProps = ToolbarDropdownItemInteractionProps & {
	ariaKeyshortcuts?: string;
	children?: React.ReactNode;
	elemAfter?: ReactNode;
	elemAfterText?: ReactNode;
	elemBefore?: ReactNode;
	hasNestedDropdownMenu?: boolean;
	href?: string;
	isDisabled?: boolean;
	isSelected?: boolean;
	onClick?: (e: React.MouseEvent | React.KeyboardEvent) => void;
	rel?: string;
	role?: ToolbarDropdownItemRole;
	shouldTitleWrap?: boolean;
	target?: string;
	testId?: string;
	textStyle?: TextStyle;
	title?: string;
	triggerRef?: Ref<HTMLButtonElement>;
} & DataAttributes;

type CustomDropdownMenuItemAnchorProps = CustomItemComponentProps &
	ToolbarDropdownItemInteractionProps & {
		'aria-disabled'?: boolean;
		'aria-haspopup'?: boolean;
		'aria-keyshortcuts'?: string;
		'aria-pressed'?: boolean;
		href: string;
		itemRole?: ToolbarDropdownItemRole;
		rel?: string;
		target?: string;
		title?: string;
	} & DataAttributes;

const CustomDropdownMenuItemAnchor = forwardRef<
	HTMLAnchorElement,
	CustomDropdownMenuItemAnchorProps
>(
	(
		{
			children,
			'data-testid': testId,
			'aria-haspopup': ariaHasPopup,
			'aria-disabled': ariaDisabled,
			'aria-pressed': ariaPressed,
			'aria-keyshortcuts': ariaKeyshortcuts,
			onClick,
			onBlur,
			onFocus,
			onMouseEnter,
			onMouseLeave,
			tabIndex,
			href,
			target,
			rel,
			title,
			itemRole,
			...dataAttributes
		},
		ref,
	) => (
		<Anchor
			role={
				expValEquals('platform_editor_layout_column_menu', 'isEnabled', true)
					? itemRole || 'menuitem'
					: expValEquals('platform_editor_enghealth_a11y_jan_fixes', 'isEnabled', true)
						? 'menuitem'
						: undefined
			}
			testId={testId as string}
			xcss={cx(
				styles.toolbarDropdownItem,
				styles.anchor,
				ariaDisabled ? styles.disabled : ariaPressed ? styles.selected : styles.enabled,
			)}
			onClick={onClick}
			onBlur={onBlur}
			onFocus={onFocus}
			onMouseEnter={onMouseEnter}
			onMouseLeave={onMouseLeave}
			tabIndex={tabIndex}
			aria-haspopup={ariaHasPopup}
			aria-expanded={ariaHasPopup ? (ariaPressed ? true : false) : undefined}
			aria-checked={
				expValEquals('platform_editor_layout_column_menu', 'isEnabled', true) &&
				(itemRole === 'menuitemradio' || itemRole === 'menuitemcheckbox')
					? ariaPressed
					: undefined
			}
			// platform_editor_enghealth_a11y_jan_fixes: menuitem roles cannot have aria-pressed attribute
			aria-pressed={
				expValEquals('platform_editor_layout_column_menu', 'isEnabled', true)
					? undefined
					: expValEquals('platform_editor_enghealth_a11y_jan_fixes', 'isEnabled', true)
						? undefined
						: ariaPressed
			}
			aria-disabled={ariaDisabled}
			aria-keyshortcuts={ariaKeyshortcuts}
			data-toolbar-component="menu-item"
			ref={ref}
			href={href}
			target={target}
			rel={rel}
			title={title}
			// eslint-disable-next-line react/jsx-props-no-spreading
			{...dataAttributes}
		>
			{children}
		</Anchor>
	),
);

export const ToolbarDropdownItem = ({
	onClick,
	onBlur,
	onFocus,
	onMouseEnter,
	onMouseLeave,
	elemBefore,
	elemAfter,
	elemAfterText,
	isSelected,
	children,
	isDisabled,
	hasNestedDropdownMenu,
	triggerRef,
	title,
	shouldTitleWrap = true,
	testId,
	ariaKeyshortcuts,
	href,
	target,
	rel,
	role,
	...dataAttributes
}: ToolbarDropdownItemProps): React.JSX.Element => {
	const parentContext = useToolbarDropdownMenu();

	const injectedElemAfter = (() => {
		if (!isValidElement(elemAfter)) {
			return elemAfter;
		}

		const elementProps = elemAfter.props as Record<string, unknown>;
		if ('isDisabled' in elementProps) {
			return elemAfter;
		}

		return cloneElement(elemAfter, {
			...elementProps,
			isDisabled,
		} as typeof elemAfter.props);
	})();

	return (
		<DropdownItem
			// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
			onClick={(e) => {
				if (!hasNestedDropdownMenu) {
					parentContext?.closeMenu(e);
				}
				onClick?.(e);
			}}
			onBlur={onBlur}
			onFocus={onFocus}
			onMouseEnter={onMouseEnter}
			onMouseLeave={onMouseLeave}
			elemBefore={elemBefore}
			elemAfter={injectedElemAfter}
			isSelected={isSelected}
			isDisabled={isDisabled}
			aria-haspopup={hasNestedDropdownMenu}
			aria-pressed={isSelected}
			aria-keyshortcuts={ariaKeyshortcuts}
			itemRole={role}
			ref={triggerRef}
			href={href}
			target={target}
			rel={rel}
			// @ts-ignore -- This `CustomDropdownMenuItemButton` has type conflicts with the `DropdownItem` component in a way that cannot be reconciled (ignored as it fails types in Jira and should in Platform)
			component={href ? CustomDropdownMenuItemAnchor : CustomDropdownMenuItemButton}
			testId={testId}
			data-toolbar-component="menu-item"
			title={title}
			shouldTitleWrap={shouldTitleWrap}
			// eslint-disable-next-line react/jsx-props-no-spreading
			{...dataAttributes}
		>
			{children}
			{elemAfterText ? (
				<Box as="span" xcss={styles.elemAfterText}>
					{elemAfterText}
				</Box>
			) : null}
		</DropdownItem>
	);
};
